import { buildCsv, downloadText } from '@/adapters/csvExport';
import { fmtNumberFlex, round } from '@/domain/numbers';
import { formatRange } from '@/domain/time';
import { can, roleLabel } from '@/domain/roles';
import { presentText } from './present';
import { ACTIVITY_LABELS, COVERAGE_LABELS, DAY_KIND_LABELS, DEVIATION_STATUS_LABELS, FIELD_LABELS, MACHINE_TYPE_LABELS, PROVENANCE_LABELS, QUALITY_LABELS, SEVERITY_LABELS, UNAVAILABLE_SHORT } from './labels';

/**
 * Felles presisjonsregel for aggregerte eksporter (P0-2):
 * beregningene skjer uavrundet i motoren; aggregerte CSV-er avrundes til samme dokumenterte
 * visningspresisjon som portalen, slik at UI, CSV og utskrift viser identiske tall for samme utvalg.
 * Kildedata-eksporter (rå poster) avrundes ikke utover fjerning av flyttallsstøy.
 */
export const EXPORT_DECIMALS = {
  tonnes: 0,
  hours: 1,
  hoursFine: 2,
  litres: 0,
  litresPerTonne: 3,
  percent: 1,
  rate: 0,
  co2Kg: 0,
  co2PerTonne: 3,
  fraction: 2,
};

const ROUNDING_NOTE = 'Avrunding: aggregerte verdier er avrundet til dokumentert visningspresisjon (samme som i portalen) og beregnet fra uavrundede summer. Forholdstall = summert teller / summert nevner.';

function meta(r, role, title, extra = []) {
  const f = [];
  if (r.scope === 'all') f.push('Omfang: alle maskiner');
  else f.push('Omfang: produksjonsområde (8 maskiner)');
  if (r.machineIds?.length) f.push(`Maskin: ${r.machineIds.map(presentText).join(', ')}`);
  if (r.fractionId) f.push(`Fraksjon: ${r.fractionId}`);
  return {
    title,
    period: `${formatRange(r.fromDate, r.toDate)} (${r.fromDate} – ${r.toDate}, Europe/Oslo; UTC [${r.current.fromUtc}, ${r.current.toUtc}))`,
    comparison: `${formatRange(r.compare.fromDate, r.compare.toDate)} (${r.compare.fromDate} – ${r.compare.toDate})`,
    filters: f,
    scenario: `${r.scenario.name} (${r.scenario.id}, ${presentText(r.scenario.seedVersion)})`,
    generatedAt: r.scenario.frozenNow,
    role: roleLabel(role),
    extra,
  };
}

function stamp(r) {
  return `${r.scenarioId}_${r.fromDate}${r.toDate !== r.fromDate ? `_${r.toDate}` : ''}`;
}

/** Én KPI-celle: utilgjengelig som tekst (aldri 0), ellers verdi med måltallets dokumenterte desimaler (identisk med portalen). */
export function metricCell(m) {
  if (!m || m.status === 'unavailable' || m.value === null) return UNAVAILABLE_SHORT[m?.reason] ?? 'Utilgjengelig';
  const v = round(m.value, m.decimals);
  return v < 0 ? v : v.toFixed(m.decimals).replace('.', ',');
}

/** Dekningslinje til metadata – samme tekst i rapport, CSV og utskrift. */
export function coverageLine(coverage) {
  return coverage.map((c) => `${FIELD_LABELS[c.field] ?? c.field} ${c.received}/${c.expected} (${c.countsLabel})`).join('; ');
}

/** CO₂-grunnlag til metadata – samme tekst i rapport, CSV og utskrift. */
export function co2Lines(c) {
  const co2 = c.co2;
  return [
    `CO2-opphav: ${co2.sourceLabel}`,
    `CO2-metode: ${co2.method}`,
    `CO2-faktor: ${fmtNumberFlex(co2.factor, 3)} ${co2.factorUnit} – ${co2.factorSource}`,
    `CO2-prioritet: ${co2.priorityRule}`,
    `CO2-intervaller: ${co2.reportedIntervals} rapportert, ${co2.estimatedIntervals} estimert, ${co2.missingIntervals} mangler (av ${co2.totalIntervals})`,
  ];
}

/** Perioderapport: KPI-er med sammenligning. */
export function exportReportCsv(r, role) {
  const rows = r.comparison.rows.filter((row) => row.key !== 'costPerTonne' || can(role, 'view_costs'));
  const csv = buildCsv(
    rows,
    [
      { key: 'label', header: 'Måltall', get: (x) => x.label },
      { key: 'unit', header: 'Enhet', get: (x) => x.unit },
      { key: 'cur', header: 'Valgt periode', get: (x) => metricCell(x.current) },
      { key: 'ref', header: 'Referanse', get: (x) => metricCell(x.reference) },
      { key: 'abs', header: 'Endring (absolutt)', get: (x) => (x.status === 'ok' || x.status === 'cannot' ? (x.absChange === null ? '' : round(x.absChange, x.decimals)) : x.message ?? '') },
      { key: 'pct', header: 'Endring', unit: '%', decimals: 2, get: (x) => (x.status === 'ok' && !x.isShare ? x.pctChange : x.status === 'cannot' ? 'kan ikke beregnes' : '') },
      { key: 'pp', header: 'Endring', unit: 'pp', decimals: 2, get: (x) => (x.isShare && x.status === 'ok' ? x.ppChange : '') },
      { key: 'status', header: 'Status', get: (x) => (x.current.status === 'unavailable' ? x.current.message : x.current.status === 'partial' ? 'registrert, delvis' : 'ok') },
      { key: 'prov', header: 'Opphav', get: (x) => (x.provenance ? PROVENANCE_LABELS[x.provenance] : x.key.startsWith('co2') ? 'utilgjengelig' : 'målt') },
      { key: 'ill', header: 'Illustrativ', get: (x) => (x.isIllustrative ? 'ja – antatt sats, ikke kundens sats' : 'nei') },
    ],
    meta(r, role, 'Perioderapport', [
      `Datadekning: ${coverageLine(r.current.coverage)}`,
      `Satser: ${fmtNumberFlex(r.current.rates.dieselNokPerLitre, 2)} NOK/L diesel, ${fmtNumberFlex(r.current.rates.machineNokPerHour, 2)} NOK/maskintime, ${fmtNumberFlex(r.current.rates.co2KgPerLitre, 3)} ${r.current.rates.co2FactorUnit} (antatte satser – forutsetninger, ikke kundens eller verifiserte lokale verdier)`,
      ...co2Lines(r.current),
      can(role, 'view_costs') ? 'Kostnad er illustrativ og ikke reell margin eller komplett kostpris' : 'Kostnadsrader er utelatt for valgt rolle',
      ROUNDING_NOTE,
    ]),
  );
  downloadText(`hardanger-rock_rapport_${stamp(r)}.csv`, csv);
}

/** Dagstabell. */
export function exportDaysCsv(r, role, days) {
  const D = EXPORT_DECIMALS;
  const partial = (v, complete, decimals) => (complete ? v : `${String(round(v, decimals)).replace('.', ',')} (delvis)`);
  const csv = buildCsv(
    days ?? r.current.days,
    [
      { key: 'date', header: 'Dato', get: (d) => d.date },
      { key: 'kind', header: 'Dagtype', get: (d) => DAY_KIND_LABELS[d.kind] ?? d.kind },
      { key: 't', header: 'Produsert', unit: 't', decimals: D.tonnes, get: (d) => (d.kind === 'planned_off' ? '' : d.tonnes) },
      ...Object.keys(r.current.tonnesByFraction).map((f) => ({ key: f, header: `Produsert ${f}`, unit: 't', decimals: D.tonnes, get: (d) => d.tonnesByFraction[f] ?? '' })),
      { key: 'ph', header: 'Produksjonstid', unit: 'h', decimals: D.hours, get: (d) => (d.productionHours === null ? 'ikke målt' : d.productionHours) },
      { key: 'sh', header: 'Stopptid', unit: 'h', decimals: D.hours, get: (d) => (d.stopHours === null ? 'ikke målt' : d.stopHours) },
      { key: 'rate', header: 'Produksjonstakt', unit: 't/h', decimals: D.rate, get: (d) => d.productionRate ?? 'kan ikke beregnes' },
      { key: 'e', header: 'Maskintimer', unit: 'h', decimals: D.hours, get: (d) => partial(d.engineHours, d.engineComplete, D.hours) },
      { key: 'i', header: 'Tomgang', unit: 'h', decimals: D.hours, get: (d) => partial(d.idleHours, d.idleComplete, D.hours) },
      { key: 'f', header: 'Diesel', unit: 'L', decimals: D.litres, get: (d) => partial(d.fuelLitres, d.fuelComplete, D.litres) },
      { key: 'lpt', header: 'Diesel per tonn', unit: 'L/t', decimals: D.litresPerTonne, get: (d) => d.litresPerTonne ?? 'utilgjengelig' },
      { key: 'is', header: 'Tomgangsandel', unit: '%', decimals: D.percent, get: (d) => d.idleShare ?? 'utilgjengelig' },
      { key: 'co2', header: 'CO₂', unit: 'kg', decimals: D.co2Kg, get: (d) => (d.co2Kg === null ? (d.kind === 'planned_off' ? '' : 'utilgjengelig') : d.co2Kg) },
      { key: 'cov', header: 'Dekning', get: (d) => COVERAGE_LABELS[d.coverage] ?? d.coverage },
    ],
    meta(r, role, 'Dagstabell', [`Datadekning: ${coverageLine(r.current.coverage)}`, ...co2Lines(r.current), 'Produksjonstid/stopptid: «ikke målt» når linjetilstand mangler for dagen – aldri 0 h.', ROUNDING_NOTE]),
  );
  downloadText(`hardanger-rock_dager_${stamp(r)}.csv`, csv);
}

/** Maskintabell. */
export function exportMachinesCsv(r, role) {
  const D = EXPORT_DECIMALS;
  const co2SourceLabel = (s) => (s === 'unavailable' ? 'utilgjengelig' : PROVENANCE_LABELS[s] ?? s);
  const csv = buildCsv(
    r.current.machines,
    [
      { key: 'id', header: 'Maskin-ID', get: (m) => presentText(m.machineId) },
      { key: 'name', header: 'Navn', get: (m) => m.name },
      { key: 'type', header: 'Type', get: (m) => MACHINE_TYPE_LABELS[m.type] ?? m.type },
      { key: 'act', header: 'Aktivitet', get: (m) => ACTIVITY_LABELS[m.activity] ?? m.activity },
      { key: 'scope', header: 'I beregningsområdet', get: (m) => (m.inScope ? 'ja' : 'nei') },
      { key: 'e', header: 'Driftstimer', unit: 'h', decimals: D.hoursFine, get: (m) => m.engineHours },
      { key: 'i', header: 'Tomgangstimer', unit: 'h', decimals: D.hoursFine, get: (m) => m.idleHours },
      { key: 'is', header: 'Tomgangsandel', unit: '%', decimals: D.percent, get: (m) => m.idleShare ?? 'utilgjengelig' },
      { key: 'f', header: 'Diesel', unit: 'L', decimals: D.litres, get: (m) => m.fuelLitres },
      { key: 'fs', header: 'Andel av diesel', unit: '%', decimals: D.percent, get: (m) => m.fuelShare ?? '' },
      { key: 'fpt', header: 'Bidrag per samlet produsert tonn', unit: 'L/t', decimals: D.litresPerTonne, get: (m) => m.fuelPerTotalTonne ?? '' },
      { key: 'co2', header: 'CO₂', unit: 'kg', decimals: D.co2Kg, get: (m) => (m.co2Kg === null ? 'utilgjengelig' : m.co2Kg) },
      { key: 'co2src', header: 'CO₂-opphav', get: (m) => co2SourceLabel(m.co2Source) },
      { key: 'alloc', header: 'Tilordnet produksjonsandel', decimals: D.fraction, get: (m) => m.allocationFraction },
      { key: 'q', header: 'Kvalitet', get: (m) => (m.quality === 'complete' ? 'Komplett' : m.quality === 'partial' ? 'Delvis' : m.quality === 'quarantined' ? 'Karantene' : 'Ingen data') },
    ],
    meta(r, role, 'Maskintabell', ['Bidrag per samlet produsert tonn er ikke maskinens egen tonnproduktivitet', ...co2Lines(r.current), ROUNDING_NOTE]),
  );
  downloadText(`hardanger-rock_maskiner_${stamp(r)}.csv`, csv);
}

/** Kildedata: maskinintervaller i perioden (normalisert importformat, uavrundet). */
export function exportSourceDataCsv(r, role, dataset) {
  const rows = dataset.machineIntervals.filter((m) => m.fromUtc >= r.current.fromUtc && m.fromUtc < r.current.toUtc && (!r.machineIds?.length || r.machineIds.includes(m.machineId)));
  const csv = buildCsv(
    rows,
    [
      { key: 'record_id', header: 'record_id', get: (m) => presentText(m.sourceRecordId) },
      { key: 'machine_id', header: 'machine_id', get: (m) => presentText(m.machineId) },
      { key: 'from_utc', header: 'from_utc', get: (m) => m.fromUtc },
      { key: 'to_utc', header: 'to_utc', get: (m) => m.toUtc },
      { key: 'engine_hours', header: 'engine_hours', get: (m) => m.engineHours },
      { key: 'idle_hours', header: 'idle_hours', get: (m) => m.idleHours },
      { key: 'fuel_litres', header: 'fuel_litres', get: (m) => m.fuelLitres },
      { key: 'reported_co2_kg', header: 'reported_co2_kg', get: (m) => m.reportedCo2Kg ?? null },
      { key: 'quality', header: 'quality', get: (m) => m.quality },
      { key: 'revision', header: 'revision', get: (m) => m.revision },
      { key: 'source', header: 'source', get: (m) => m.sourceId },
    ],
    meta(r, role, 'Kildedata – maskinintervaller', [
      'Kolonner følger importkontrakten (record_id;machine_id;from_utc;to_utc;engine_hours;idle_hours;fuel_litres;reported_co2_kg;quality;revision;source)',
      'Tomme celler = null (status missing). Målt 0 er en gyldig verdi. Verdier er uavrundede kildeverdier (maks 4 desimaler).',
    ]),
  );
  downloadText(`hardanger-rock_kildedata_maskin_${stamp(r)}.csv`, csv);
}

/** Kildedata: produksjon (uavrundet). */
export function exportProductionSourceCsv(r, role, dataset) {
  const rows = dataset.production.filter((p) => p.fromUtc >= r.current.fromUtc && p.fromUtc < r.current.toUtc);
  const csv = buildCsv(
    rows,
    [
      { key: 'record_id', header: 'record_id', get: (p) => presentText(p.sourceRecordId) },
      { key: 'line_id', header: 'line_id', get: (p) => p.lineId },
      { key: 'from_utc', header: 'from_utc', get: (p) => p.fromUtc },
      { key: 'to_utc', header: 'to_utc', get: (p) => p.toUtc },
      { key: 'fraction_id', header: 'fraction_id', get: (p) => p.fractionId },
      { key: 'produced_tonnes', header: 'produced_tonnes', get: (p) => p.producedTonnes },
      { key: 'quality', header: 'quality', get: (p) => QUALITY_LABELS[p.quality] ?? p.quality },
      { key: 'revision', header: 'revision', get: (p) => p.revision },
    ],
    meta(r, role, 'Kildedata – produksjon', ['Produsert tonn kommer kun fra produksjonskilden (PLS). Lastet/levert mengde er egen kontekstkilde og legges aldri til.']),
  );
  downloadText(`hardanger-rock_kildedata_produksjon_${stamp(r)}.csv`, csv);
}

export function exportDeviationsCsv(r, role, statusOf) {
  const csv = buildCsv(
    r.deviations,
    [
      { key: 'id', header: 'Avviks-ID', get: (d) => presentText(d.id) },
      { key: 'sev', header: 'Alvorlighet', get: (d) => SEVERITY_LABELS[d.severity] },
      { key: 'rule', header: 'Regel', get: (d) => d.ruleLabel },
      { key: 'period', header: 'Periode', get: (d) => `${d.period.fromDate} – ${d.period.toDate}` },
      { key: 'scope', header: 'Berørt maskin/kilde', get: (d) => presentText(d.scope.machineId ?? d.scope.sourceId ?? 'Linje') },
      { key: 'obs', header: 'Observert endring', get: (d) => presentText(d.observed) },
      { key: 'status', header: 'Status', get: (d) => DEVIATION_STATUS_LABELS[statusOf(d.id)] },
    ],
    meta(r, role, 'Avviksliste'),
  );
  downloadText(`hardanger-rock_avvik_${stamp(r)}.csv`, csv);
}
